import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { useAllTrips } from '../hooks/useAllTrips';
import type { RecentTrip } from '../api/trip';

type Props = {
  onBack: () => void;
};

const HistoryListPanel = ({ onBack }: Props): JSX.Element => {
  const { theme } = useAITTheme();
  const history = useHistory();
  const { trips, loading, loadingMore, hasMore, loadMore } = useAllTrips();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const handleNavigateToTrip = (tripId: string) => {
    history.push(`/trips/${tripId}/ai_messages`);
    onBack();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button type="button" style={styles.backButton} onClick={onBack}>
          ← 戻る
        </button>
        <h2 style={styles.title}>チャット履歴</h2>
      </div>
      <div style={styles.content}>
        {loading ? (
          <div style={styles.loadingContainer}>
            <Loading />
          </div>
        ) : trips.length === 0 ? (
          <div style={styles.emptyMessage}>履歴がありません</div>
        ) : (
          <div style={styles.list}>
            {trips.map(trip => (
              <HistoryItem
                key={trip.tripId}
                trip={trip}
                formatDate={formatDate}
                onNavigate={handleNavigateToTrip}
                theme={theme}
              />
            ))}
            {hasMore && (
              <button type="button" style={styles.loadMoreButton} onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? '読み込み中...' : 'もっと見る'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

type HistoryItemProps = {
  trip: RecentTrip;
  formatDate: (dateString: string) => string;
  onNavigate: (tripId: string) => void;
  theme: AITTheme;
};

const HistoryItem = ({ trip, formatDate, onNavigate, theme }: HistoryItemProps): JSX.Element => {
  const styles = useMemo(() => getItemStyles(theme, trip.draft), [theme, trip.draft]);

  return (
    <button type="button" style={styles.item} onClick={() => onNavigate(trip.tripId)}>
      <div style={styles.itemContent}>
        <div style={styles.message}>{trip.lastMessage || '(メッセージなし)'}</div>
        <div style={styles.meta}>
          <span style={styles.date}>{formatDate(trip.createdAt)}</span>
        </div>
      </div>
    </button>
  );
};

const getStyles = (theme: AITTheme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
    backgroundColor: theme.whiteColor,
    minHeight: 0,
    overflow: 'hidden' as const
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.grayBorderColor}`,
    backgroundColor: theme.whiteColor
  },
  backButton: {
    background: 'none',
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '4px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    color: theme.textColor
  },
  title: {
    fontSize: '18px',
    fontWeight: 600 as const,
    margin: 0,
    color: theme.textColor
  },
  content: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '24px'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px'
  },
  emptyMessage: {
    textAlign: 'center' as const,
    color: theme.grayTextColor,
    padding: '40px'
  },
  list: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px'
  },
  loadMoreButton: {
    padding: '12px 24px',
    fontSize: '14px',
    color: theme.linkColor,
    backgroundColor: 'transparent',
    border: `1px solid ${theme.linkColor}`,
    borderRadius: '8px',
    cursor: 'pointer',
    marginTop: '8px'
  }
});

const getItemStyles = (theme: AITTheme, isDraft: boolean) => ({
  item: {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box' as const,
    padding: '16px 20px',
    backgroundColor: isDraft ? '#e6f4fb' : '#f9f9f9',
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '8px',
    cursor: 'pointer',
    textAlign: 'left' as const,
    transition: 'background-color 0.2s'
  },
  itemContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    minWidth: 0
  },
  message: {
    fontSize: '14px',
    color: theme.textColor,
    lineHeight: 1.5,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  date: {
    fontSize: '12px',
    color: theme.grayTextColor
  }
});

export default HistoryListPanel;
